import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { Transition, Dialog } from '@headlessui/react';
import { Fragment } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

function Parts() {

  document.title = 'Parts';

  const { user } = useAuth();
  const [inventoryData, setInventoryData] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [shops, setShops] = useState([]);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [createPartModalOpen, setCreatePartModalOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [partHistory, setPartHistory] = useState({ productionLogs: [], deliveries: [] });
  const [newPart, setNewPart] = useState({
    part_number_master: '',
    revision: '',
    description: '',
    inventory_quantity: '',
    minimum_stock_level: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      setLoading(true);
      try {
        const { data: inventory, error: invError } = await supabase
          .from('inventory_insights')
          .select('*');
        if (invError) throw invError;

        const { data: shopsData, error: shopsError } = await supabase
          .from('shops')
          .select('id, name');
        if (shopsError) throw shopsError;
        setShops(shopsData || []);

        setInventoryData(inventory);
        setFilteredInventory(inventory);
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error(`Failed to fetch inventory data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);

    const sortedInventory = [...filteredInventory].sort((a, b) => {
      const aValue = a[field] || 0;
      const bValue = b[field] || 0;
      if (field === 'associated_shops') {
        const aShops = a[field]?.join(', ') || '';
        const bShops = b[field]?.join(', ') || '';
        return newDirection === 'asc' ? aShops.localeCompare(bShops) : bShops.localeCompare(aShops);
      }
      if (typeof aValue === 'number') return newDirection === 'asc' ? aValue - bValue : bValue - aValue;
      return newDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
    setFilteredInventory(sortedInventory);
  };

  const handleFilter = () => {
    let filtered = [...inventoryData];
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.part_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredInventory(filtered);
  };

  useEffect(() => {
    handleFilter();
  }, [searchTerm]);

  const viewPartHistory = async (part_number) => {
    try {
      const { data: productionLogs, error: prodError } = await supabase
        .from('production_logs')
        .select('quantity_produced, production_date, shop_id, shops(name)')
        .eq('part_number', part_number)
        .order('production_date', { ascending: false })
        .limit(10);
      if (prodError) throw prodError;

      const { data: deliveries, error: delError } = await supabase
        .from('deliveries')
        .select('quantity, ship_date')
        .eq('part_number', part_number)
        .order('ship_date', { ascending: false })
        .limit(10);
      if (delError) throw delError;

      setPartHistory({ productionLogs: productionLogs || [], deliveries: deliveries || [] });
      setSelectedPart(part_number);
      setHistoryModalOpen(true);
    } catch (err) {
      console.error('Error fetching part history:', err);
      toast.error(`Failed to fetch part history: ${err.message}`);
    }
  };

  const getStockStatusClass = (availableStock, minimumStockLevel) => {
    if (availableStock <= 0) return 'bg-red-100 text-red-700';
    if (availableStock < minimumStockLevel) return 'bg-amber-100 text-amber-700';
    return 'bg-emerald-100 text-emerald-700';
  };

  const validateForm = () => {
    const errors = {};
    if (!newPart.part_number_master.trim()) {
      errors.part_number_master = 'Part number master is required';
    }
    if (!newPart.description.trim()) {
      errors.description = 'Description is required';
    }
    if (newPart.inventory_quantity === '' || newPart.inventory_quantity < 0) {
      errors.inventory_quantity = 'Inventory quantity must be a non-negative number';
    }
    if (newPart.minimum_stock_level === '' || newPart.minimum_stock_level < 0) {
      errors.minimum_stock_level = 'Minimum stock level must be a non-negative number';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreatePart = async () => {
    if (!validateForm()) return;

    try {
      const { error } = await supabase
        .from('parts')
        .insert({
          part_number_master: newPart.part_number_master.trim(),
          revision: newPart.revision.trim() || null,
          description: newPart.description.trim(),
          inventory_quantity: parseInt(newPart.inventory_quantity, 10),
          minimum_stock_level: parseInt(newPart.minimum_stock_level, 10),
        });
      if (error) throw new Error(`Failed to create part: ${error.message}`);

      const { data: updatedInventory, error: invError } = await supabase
        .from('inventory_insights')
        .select('*');
      if (invError) throw invError;

      setInventoryData(updatedInventory);
      setFilteredInventory(updatedInventory);

      setNewPart({
        part_number_master: '',
        revision: '',
        description: '',
        inventory_quantity: '',
        minimum_stock_level: '',
      });
      setFormErrors({});
      setCreatePartModalOpen(false);
      toast.success('Part created successfully!');
    } catch (err) {
      console.error('Error creating part:', err);
      toast.error(`Failed to create part: ${err.message}`);
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc'
      ? <ChevronUpIcon className="w-4 h-4 inline ml-1" />
      : <ChevronDownIcon className="w-4 h-4 inline ml-1" />;
  };

  const totalParts = inventoryData.length;
  const lowStockCount = inventoryData.filter(item => item.stock_status === 'Low Stock').length;
  const outOfStockCount = inventoryData.filter(item => item.stock_status === 'Out of Stock').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="p-4">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-lg font-semibold text-slate-800">Parts Inventory</h1>
            <p className="text-xs text-slate-500 mt-0.5">Manage parts and inventory levels</p>
          </div>
          <button
            onClick={() => setCreatePartModalOpen(true)}
            className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
          >
            Create Part
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total Parts</p>
            <p className="text-2xl font-semibold text-slate-800 tabular-nums">{totalParts}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Low Stock</p>
            <p className="text-2xl font-semibold text-amber-600 tabular-nums">{lowStockCount}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Out of Stock</p>
            <p className="text-2xl font-semibold text-red-600 tabular-nums">{outOfStockCount}</p>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by part number or description..."
            className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
          />
        </div>

        {/* Inventory Table */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('part_number')}
                >
                  Part Number <SortIcon field="part_number" />
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Description
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('shop')}
                >
                  Shop <SortIcon field="shop" />
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('inventory_quantity')}
                >
                  Current <SortIcon field="inventory_quantity" />
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('drawdown')}
                >
                  Drawdown <SortIcon field="drawdown" />
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('available_stock')}
                >
                  Available <SortIcon field="available_stock" />
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredInventory.map((item) => (
                <tr key={item.part_number} className="hover:bg-slate-50 transition-colors">
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button
                      onClick={() => navigate(`/part/${item.part_number}`)}
                      className="text-sm font-medium text-slate-800 hover:text-blue-600 transition-colors"
                    >
                      {item.part_number}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-600 truncate max-w-xs" title={item.description}>
                    {item.description}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">
                    {item.shop || '-'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">
                    {item.inventory_quantity}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">
                    {item.drawdown}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium ${getStockStatusClass(item.available_stock, item.minimum_stock_level)}`}>
                      {item.available_stock}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button
                      onClick={() => viewPartHistory(item.part_number)}
                      className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
                    >
                      History
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredInventory.length === 0 && (
            <p className="px-3 py-4 text-sm text-slate-500 text-center">No parts found.</p>
          )}
        </div>

        {/* Part History Modal */}
        <Transition appear show={historyModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setHistoryModalOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                      <Dialog.Title className="text-sm font-semibold text-slate-800">
                        Part History: {selectedPart}
                      </Dialog.Title>
                      <button onClick={() => setHistoryModalOpen(false)} className="text-slate-400 hover:text-slate-600">×</button>
                    </div>
                    <div className="p-4 space-y-4">
                      <div>
                        <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Recent Production</h4>
                        {partHistory.productionLogs.length > 0 ? (
                          <table className="w-full">
                            <thead>
                              <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Quantity</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Date</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Shop</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {partHistory.productionLogs.map((log, index) => (
                                <tr key={index}>
                                  <td className="px-3 py-2 text-sm text-slate-600 tabular-nums">{log.quantity_produced}</td>
                                  <td className="px-3 py-2 text-sm text-slate-600 tabular-nums">{new Date(log.production_date).toLocaleDateString()}</td>
                                  <td className="px-3 py-2 text-sm text-slate-600">{log.shops?.name || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="text-sm text-slate-500">No production logs.</p>
                        )}
                      </div>

                      <div>
                        <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Recent Deliveries</h4>
                        {partHistory.deliveries.length > 0 ? (
                          <table className="w-full">
                            <thead>
                              <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Quantity</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Ship Date</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {partHistory.deliveries.map((delivery, index) => (
                                <tr key={index}>
                                  <td className="px-3 py-2 text-sm text-slate-600 tabular-nums">{delivery.quantity}</td>
                                  <td className="px-3 py-2 text-sm text-slate-600 tabular-nums">{new Date(delivery.ship_date).toLocaleDateString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="text-sm text-slate-500">No deliveries.</p>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-end px-4 py-3 border-t border-slate-100 bg-slate-50">
                      <button
                        onClick={() => setHistoryModalOpen(false)}
                        className="px-3 py-1.5 text-sm text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-100 transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Create Part Modal */}
        <Transition appear show={createPartModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setCreatePartModalOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                      <Dialog.Title className="text-sm font-semibold text-slate-800">
                        Create New Part
                      </Dialog.Title>
                      <button onClick={() => setCreatePartModalOpen(false)} className="text-slate-400 hover:text-slate-600">×</button>
                    </div>
                    <div className="p-4 space-y-3">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Part Number Master</label>
                        <input
                          type="text"
                          value={newPart.part_number_master}
                          onChange={(e) => setNewPart({ ...newPart, part_number_master: e.target.value })}
                          className={`w-full px-2 py-1.5 text-sm border ${formErrors.part_number_master ? 'border-red-300' : 'border-slate-200'} rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400`}
                          placeholder="e.g., 25-1234"
                        />
                        {formErrors.part_number_master && (
                          <p className="mt-1 text-xs text-red-500">{formErrors.part_number_master}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Revision</label>
                        <input
                          type="text"
                          value={newPart.revision}
                          onChange={(e) => setNewPart({ ...newPart, revision: e.target.value })}
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                          placeholder="e.g., 1, A, or leave blank"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Description</label>
                        <textarea
                          value={newPart.description}
                          onChange={(e) => setNewPart({ ...newPart, description: e.target.value })}
                          className={`w-full px-2 py-1.5 text-sm border ${formErrors.description ? 'border-red-300' : 'border-slate-200'} rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400`}
                          placeholder="Enter description"
                          rows="2"
                        />
                        {formErrors.description && (
                          <p className="mt-1 text-xs text-red-500">{formErrors.description}</p>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Initial Quantity</label>
                          <input
                            type="number"
                            value={newPart.inventory_quantity}
                            onChange={(e) => setNewPart({ ...newPart, inventory_quantity: e.target.value })}
                            className={`w-full px-2 py-1.5 text-sm border ${formErrors.inventory_quantity ? 'border-red-300' : 'border-slate-200'} rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums`}
                            placeholder="0"
                            min="0"
                          />
                          {formErrors.inventory_quantity && (
                            <p className="mt-1 text-xs text-red-500">{formErrors.inventory_quantity}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Min Stock Level</label>
                          <input
                            type="number"
                            value={newPart.minimum_stock_level}
                            onChange={(e) => setNewPart({ ...newPart, minimum_stock_level: e.target.value })}
                            className={`w-full px-2 py-1.5 text-sm border ${formErrors.minimum_stock_level ? 'border-red-300' : 'border-slate-200'} rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums`}
                            placeholder="0"
                            min="0"
                          />
                          {formErrors.minimum_stock_level && (
                            <p className="mt-1 text-xs text-red-500">{formErrors.minimum_stock_level}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50">
                      <button
                        onClick={() => {
                          setNewPart({
                            part_number_master: '',
                            revision: '',
                            description: '',
                            inventory_quantity: '',
                            minimum_stock_level: '',
                          });
                          setFormErrors({});
                          setCreatePartModalOpen(false);
                        }}
                        className="px-3 py-1.5 text-sm text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-100 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreatePart}
                        className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
                      >
                        Create Part
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </div>
  );
}

export default Parts;
